import { useEffect } from "react";

const DEFAULT_TITLE = "RC Chemical Labs — Premium Laboratory Research Supplies";
const DEFAULT_DESCRIPTION = "Trusted laboratory & industrial chemical supplier. Bulk raw materials, reagents, specialty chemicals. Fast delivery, ISO certified, competitive pricing.";
const SITE_URL = "https://www.rcchemicallab.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

const setMeta = (name: string, content: string) => {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setProperty = (property: string, content: string) => {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href: string) => {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Build a canonical URL from the current pathname. Strips query strings and
 * hash fragments, normalizes trailing slashes, and lowercases the path so
 * crawlers don't see duplicate variants.
 */
const buildCanonical = (override?: string) => {
  if (override) {
    return override.startsWith("http") ? override : `${SITE_URL}${override}`;
  }
  if (typeof window === "undefined") return SITE_URL;
  let path = window.location.pathname || "/";
  path = path.toLowerCase();
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  return `${SITE_URL}${path}`;
};

const resolveImage = (image?: string) => {
  if (!image) return DEFAULT_OG_IMAGE;
  if (image.startsWith("http")) return image;
  return `${SITE_URL}${image.startsWith("/") ? "" : "/"}${image}`;
};

export const usePageMeta = (
  title: string,
  description?: string,
  canonicalPath?: string,
  options?: { noindex?: boolean; ogType?: string; ogImage?: string },
) => {
  const noindex = options?.noindex ?? false;
  const ogType = options?.ogType ?? "website";
  const ogImage = options?.ogImage;
  useEffect(() => {
    const desc = description || DEFAULT_DESCRIPTION;
    const url = buildCanonical(canonicalPath);
    const image = resolveImage(ogImage);
    document.title = title;
    setMeta("description", desc);
    setCanonical(url);
    setProperty("og:title", title);
    setProperty("og:description", desc);
    setProperty("og:url", url);
    setProperty("og:type", ogType);
    setProperty("og:image", image);
    setProperty("og:site_name", "RC Chemical Lab");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", image);
    // Toggle robots noindex per-page. Always set explicitly so a previous
    // noindex page doesn't leak into the next route.
    setMeta("robots", noindex ? "noindex, nofollow" : "index, follow");
  }, [title, description, canonicalPath, noindex, ogType, ogImage]);
};
